const Moment = require('moment');
const { capitalize } = require('./strings');
const {
  COMMODITIES,
  CURRENCIES,
  DAYS,
  DEVICE_CHARGE_STATUS,
  DEVICE_KINDS,
  DEVICE_MFGS,
  DEVICE_SUB_BRAND,
  EVENT_CATEGORY,
  EVENT_INITIATOR,
  EVENT_TYPE,
  LINKED_ACCOUNT_TYPES,
  MONTHS,
  PAYMENT_TYPES,
  STATE_NAMES,
  STATE_NAMES_AU,
  STATE_NAMES_CA,
  TIME_ZONES,
  VEHICLE_MAKES,
} = require('../constants');
const {
  DAYS_ES,
  DEVICE_CHARGE_STATUS_ES,
  DEVICE_KINDS_ES,
  EVENT_CATEGORY_ES,
  EVENT_INITIATOR_ES,
  EVENT_TYPE_ES,
  MONTHS_ES,
  PAYMENT_TYPES_ES,
} = require('../constants_es');

function auxiliaryMultiLingualFunction(key, dicts, lang) {
  let dict = lang in dicts ? dicts[lang] : dicts['en'];
  let ret = null;
  if (typeof key === 'string') {
    if (key in dict) {
      ret =  dict[key];
      if (ret === null) {
        ret = dicts['en'][key];
      }
    } else {
      // TODO: determine if we actually want to capitalize
      // Change introduced in https://github.com/virtual-peaker/vp-common/pull/9
      // Tests elsewhere indicate that is an unexpected adjustment, so change was reverted
      // ret = capitalize(key);
      ret = key;
    }
  }
  return ret ? ret : dict.default;
}

function commodityByKey(key) {
  const co = COMMODITIES.find((commodity) => commodity.key === key);
  return co ? co.name : null;
}

function commodityByName(name) {
  const co = COMMODITIES.find((commodity) => commodity.name === name);
  return co ? co.key : null;
}

function currencyByKey(key) {
  const cu = CURRENCIES.find((currency) => currency.key === key);
  return cu ? cu.name : null;
}

function currencyByName(name) {
  const cu = CURRENCIES.find((currency) => currency.name === name);
  return cu ? cu.key : null;
}

function day(index, lang = 'en') {
  if (index < 0 || index >= DAYS.length) {
    switch(lang) {
      case 'es':
        return 'desconocido';
      default:
        return 'Unknown';
    }
  }
  switch(lang) {
    case 'es':
      return DAYS_ES[index];
    default:
      return DAYS[index];
  }
}

function deviceChargeStatus(status, lang='en') {
  return auxiliaryMultiLingualFunction(status, {en: DEVICE_CHARGE_STATUS, es: DEVICE_CHARGE_STATUS_ES}, lang);
}

function deviceKind(kind, lang='en') {
  return auxiliaryMultiLingualFunction(kind, {en: DEVICE_KINDS, es: DEVICE_KINDS_ES}, lang);
}

function deviceMfg(mfg, type) {
  if (typeof mfg === 'string') {
    if (mfg === 'smartcar' && type) {
      return vehicleMake(type);
    }
    if (mfg in DEVICE_MFGS) {
      return DEVICE_MFGS[mfg];
    }
    return capitalize(mfg);
  }
  return DEVICE_MFGS.default;
}

function deviceSubBrand(brand) {
  if (typeof brand === 'string') {
    if (brand in DEVICE_SUB_BRAND) {
      return DEVICE_SUB_BRAND[brand];
    }
    return capitalize(brand);
  }
  return DEVICE_SUB_BRAND.default;
}

function eventCategory(category, lang = 'en') {
  return auxiliaryMultiLingualFunction(category, {en: EVENT_CATEGORY, es: EVENT_CATEGORY_ES}, lang);
}

function eventInitiator(initiator, lang = 'en') {
  return auxiliaryMultiLingualFunction(initiator, {en: EVENT_INITIATOR, es: EVENT_INITIATOR_ES}, lang);
}

function eventType(type, lang = 'en') {
  return auxiliaryMultiLingualFunction(type, {en: EVENT_TYPE, es: EVENT_TYPE_ES}, lang);
}

function linkedAccountType(type) {
  if (typeof type === 'string') {
    if (type in LINKED_ACCOUNT_TYPES) {
      return LINKED_ACCOUNT_TYPES[type];
    }
    return type;
  }
  return LINKED_ACCOUNT_TYPES.default;
}

function month(index, lang = 'en') {
  if (index < 0 || index >= MONTHS.length) {
    switch(lang) {
      case 'es':
        return 'desconocido';
      default:
        return 'Unknown';
    }
  }
  switch(lang) {
    case 'es':
      return MONTHS_ES[index];
    default:
      return MONTHS[index];
  }
}

function paymentType(type, lang = 'en') {
  return auxiliaryMultiLingualFunction(type, {en: PAYMENT_TYPES, es: PAYMENT_TYPES_ES}, lang);
}

function stateByAbbreviation(abbreviation, countryCode) {
  if (typeof abbreviation === 'string') {
    let st = '';
    switch (countryCode) {
      case 'AU':
        st = STATE_NAMES_AU.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
      case 'CA':
        st = STATE_NAMES_CA.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
      default:
        st = STATE_NAMES.find((state) => state.abbreviation === abbreviation);
        return st ? st.name : capitalize(abbreviation);
    }
  }
  return 'Unknown';
}

function stateByName(name, countryCode) {
  if (typeof name === 'string') {
    const bigName = name.toUpperCase();
    let st = '';
    switch (countryCode) {
      case 'AU':
        st = STATE_NAMES_AU.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
      case 'CA':
        st = STATE_NAMES_CA.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
      default:
        st = STATE_NAMES.find((state) => state.name === bigName);
        return st ? st.abbreviation : capitalize(name);
    }
  }
  return 'Unknown';
}

function time(input) {
  const hours = Math.floor(input);
  const minutes = (input - hours) * 60;

  return `${hours.toString()}:${(`00${Math.round(minutes).toString()}`).slice(-2)}`;
}

function timeAmPm(input) {
  if (input == null) {
    return 'ALL DAY';
  }
  let hours = Math.floor(input);
  let minutes = (input - hours) * 60;
  let ampm = 'AM';

  if (hours > 12) {
    ampm = 'PM';
    hours -= 12;
  } else if (hours === 12) {
    ampm = 'PM';
  } else if (hours === 0) {
    ampm = 'AM';
    hours = 12;
  }
  minutes = Math.round(minutes);

  return hours.toString() + (minutes !== 0 ? `:${(`00${Math.round(minutes).toString()}`).slice(-2)}` : '') + ampm;
}

function timezoneByName(name) {
  if (typeof name === 'string') {
    const tz = TIME_ZONES.find((timezone) => timezone.name === name);
    return tz ? tz.value : null;
  }
  return null;
}

function timezoneByValue(value) {
  if (typeof value === 'string') {
    const tz = TIME_ZONES.find((timezone) => timezone.value === value);
    return tz ? tz.name : null;
  }
  return null;
}

function vehicleMake(brand) {
  if (typeof brand === 'string') {
    if (brand in VEHICLE_MAKES) {
      return VEHICLE_MAKES[brand];
    }
    return brand;
  }
  return VEHICLE_MAKES.default;
}

/* Auxiliary Functions (NOT EXPORTED) */
function formatDate(date, stringFormat) {
  return Moment(date).format(stringFormat);
}

function localisedDate(date, stringFormat, locale) {
  return Moment(date).locale(locale).format(stringFormat);
}

module.exports = {
  commodityByKey,
  commodityByName,
  currencyByKey,
  currencyByName,
  date: (date, stringFormat = 'L') => formatDate(date, stringFormat),
  dateTime: (date) => formatDate(date, 'LT [on] L'),
  day,
  deviceChargeStatus,
  deviceKind,
  deviceMfg,
  deviceSubBrand,
  eventCategory,
  eventInitiator,
  eventType,
  linkedAccountType,
  localizedDate: (date, stringFormat = 'L', locale = 'en') => localisedDate(date, stringFormat, locale),
  localizedDateTime: (date, locale = 'en') => localisedDate(date, 'LT [on] L', locale),
  localizedTimeDate: (date, locale = 'en') => localisedDate(date, 'LT [at] L', locale),
  month,
  paymentType,
  stateByAbbreviation: (abbreviation, countryCode = 'US') => stateByAbbreviation(abbreviation, countryCode),
  stateByName: (name, countryCode = 'US') => stateByName(name, countryCode),
  time,
  timeAmPm,
  timeDate: (date) => formatDate(date, 'LT [at] L'),
  timezoneByName,
  timezoneByValue,
  vehicleMake,
};
